"use client";
import { useState } from "react";
import {
  type BudgetCategory,
  type DayMode,
  formatMoney,
  formatDateKey,
  getCurrentWeekDays,
  initialCategories,
  formatWeekRange,
  limitTotal,
  modeLabels,
  spentTotal,
} from "@/features/budget/budget-data";

const Progress = ({
  value,
  total,
  color = "#e7a05b",
}: {
  value: number;
  total: number;
  color?: string;
}) => (
  <div className="progress-track" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:25:3"}>
    <span style={{ width: `${Math.min((value / total) * 100, 100)}%`, backgroundColor: color }} data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:26:5"} />
  </div>
);

export const BudgetDashboard = () => {
  const [categories, setCategories] = useState(initialCategories);
  const [days, setDays] = useState(getCurrentWeekDays);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState({ name: "", limit: "" });
  const [expenseDraft, setExpenseDraft] = useState({ category: "1", amount: "" });
  const totalLimit = limitTotal(categories);
  const totalSpent = spentTotal(categories);
  const todayDate = formatDateKey(new Date());
  const changeDayMode = (date: string) => {
    const modes: DayMode[] = ["blocked", "shopping", "marketplace", "personal"];
    setDays((current) =>
      current.map((day) =>
        day.date === date
          ? { ...day, mode: modes[(modes.indexOf(day.mode) + 1) % modes.length] }
          : day,
      ),
    );
  };
  const addCategory = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!categoryDraft.name || !categoryDraft.limit) return;
    setCategories((items) => [
      ...items,
      {
        id: Date.now(),
        name: categoryDraft.name,
        limit: Number(categoryDraft.limit),
        spent: 0,
        color: "#d77b68",
      },
    ]);
    setCategoryDraft({ name: "", limit: "" });
    setShowCategoryForm(false);
  };
  const addExpense = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const amount = Number(expenseDraft.amount);
    if (!amount) return;
    setCategories((items) =>
      items.map((item) =>
        item.id === Number(expenseDraft.category) ? { ...item, spent: item.spent + amount } : item,
      ),
    );
    setExpenseDraft({ category: "1", amount: "" });
    setShowExpenseForm(false);
  };
  return (
    <main className="app-shell" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:79:5"}>
      <header className="topbar" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:80:7"}>
        <div className="brand" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:81:9"}>
          <div className="brand-mark" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:82:11"}>₽</div>
          <div data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:83:11"}>
            <strong data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:84:13"}>Семейный бюджет</strong>
            <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:85:13"}>контроль расходов</span>
          </div>
        </div>
        <button className="avatar" aria-label="Профиль" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:88:9"}>
          МС
        </button>
      </header>
      <div className="content" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:92:7"}>
        <section className="greeting" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:93:9"}>
          <p className="eyebrow" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:94:11"}>С возвращением, Мария</p>
          <h1 data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:95:11"}>
            Ваш бюджет
            <br data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:97:13"} />
            <em data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:98:13"}>под контролем.</em>
          </h1>
          <p className="muted" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:100:11"}>Спокойствие начинается с ясности.</p>
        </section>
        <section className="week-card" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:102:9"}>
          <div className="section-heading" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:103:11"}>
            <div data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:104:13"}>
              <p className="eyebrow" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:105:15"}>ТЕКУЩАЯ НЕДЕЛЯ</p>
              <h2 data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:106:15"}>{formatWeekRange(days)}</h2>
            </div>
            <button className="round-button" aria-label="Выбрать неделю" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:108:13"}>
              ⌄
            </button>
          </div>
          <div className="balance-row" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:112:11"}>
            <div data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:113:13"}>
              <span className="label" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:114:15"}>Осталось на неделю</span>
              <strong className="balance" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:115:15"}>{formatMoney(totalLimit - totalSpent)}</strong>
            </div>
            <div
              className="donut"
              style={{ "--progress": `${(totalSpent / totalLimit) * 100}%` } as React.CSSProperties}
             data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:117:13"}>
              <b data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:121:15"}>{Math.round((totalSpent / totalLimit) * 100)}%</b>
              <small data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:122:15"}>потрачено</small>
            </div>
          </div>
          <Progress value={totalSpent} total={totalLimit} data-amazi-jsx-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:125:11"} />
          <div className="card-foot" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:126:11"}>
            <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:127:13"}>{formatMoney(totalSpent)} потрачено</span>
            <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:128:13"}>из {formatMoney(totalLimit)}</span>
          </div>
        </section>
        <section className="section-block" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:131:9"}>
          <div className="section-title" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:132:11"}>
            <h2 data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:133:13"}>Ритм недели</h2>
            <button className="text-button" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:134:13"}>Как это работает ⓘ</button>
          </div>
          <p className="muted small" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:136:11"}>Нажмите на день, чтобы изменить его режим</p>
          <div className="day-grid" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:137:11"}>
            {days.map((day) => (
              <button
                className={`day-cell ${day.date === todayDate ? "today" : ""}`}
                key={day.date}
                onClick={() => changeDayMode(day.date)}
               data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:139:15"}>
                <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:144:17"}>{day.weekday}</span>
                <strong data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:145:17"}>{day.number}</strong>
                <i className={`day-dot ${day.mode}`} data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:146:17"} />
                <small data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:147:17"}>{modeLabels[day.mode]}</small>
              </button>
            ))}
          </div>
          <div className="legend" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:151:11"}>
            <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:152:13"}>
              <i className="day-dot blocked" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:153:15"} />
              Не тратить
            </span>
            <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:156:13"}>
              <i className="day-dot shopping" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:157:15"} />
              Закупки
            </span>
            <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:160:13"}>
              <i className="day-dot marketplace" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:161:15"} />
              Маркетплейс
            </span>
            <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:164:13"}>
              <i className="day-dot personal" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:165:15"} />
              Личные
            </span>
          </div>
        </section>
        <section className="section-block budgets" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:170:9"}>
          <div className="section-title" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:171:11"}>
            <div data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:172:13"}>
              <h2 data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:173:15"}>Лимиты на неделю</h2>
              <p className="muted small" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:174:15"}>Категории расходов</p>
            </div>
            <button
              className="add-icon"
              onClick={() => setShowCategoryForm(true)}
              aria-label="Добавить категорию"
             data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:176:13"}>
              +
            </button>
          </div>
          <div className="budget-list" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:184:11"}>
            {categories.map((category: BudgetCategory) => (
              <div className="budget-item" key={category.id} data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:186:15"}>
                <div
                  className="category-icon"
                  style={{ backgroundColor: `${category.color}22`, color: category.color }}
                 data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:187:17"}>
                  ₽
                </div>
                <div className="budget-info" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:193:17"}>
                  <div className="budget-label" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:194:19"}>
                    <strong data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:195:21"}>{category.name}</strong>
                    <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:196:21"}>{formatMoney(category.limit)}</span>
                  </div>
                  <Progress value={category.spent} total={category.limit} color={category.color} data-amazi-jsx-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:198:19"} />
                  <div className="budget-label muted small" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:199:19"}>
                    <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:200:21"}>Потрачено {formatMoney(category.spent)}</span>
                    <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:201:21"}>{formatMoney(category.limit - category.spent)} осталось</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {showCategoryForm && (
            <form className="inline-form" onSubmit={addCategory} data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:208:13"}>
              <input
                autoFocus
                placeholder="Название категории"
                value={categoryDraft.name}
                onChange={(e) => setCategoryDraft({ ...categoryDraft, name: e.target.value })} data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:209:15"}
              />
              <input
                type="number"
                placeholder="Лимит, ₽"
                value={categoryDraft.limit}
                onChange={(e) => setCategoryDraft({ ...categoryDraft, limit: e.target.value })} data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:215:15"}
              />
              <button className="primary-button" type="submit" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:221:15"}>
                Добавить
              </button>
            </form>
          )}
        </section>
        <section className="expense-callout" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:227:9"}>
          <div className="expense-icon" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:228:11"}>＋</div>
          <div data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:229:11"}>
            <h2 data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:230:13"}>Добавить расход</h2>
            <p data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:231:13"}>Зафиксируйте трату — и всё пересчитается.</p>
          </div>
          <button onClick={() => setShowExpenseForm(true)} aria-label="Добавить расход" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:233:11"}>
            →
          </button>
        </section>
        {showExpenseForm && (
          <form className="expense-form" onSubmit={addExpense} data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:238:11"}>
            <label data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:239:13"}>
              Категория
              <select
                value={expenseDraft.category}
                onChange={(e) => setExpenseDraft({ ...expenseDraft, category: e.target.value })}
               data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:241:15"}>
                {categories.map((category) => (
                  <option value={category.id} key={category.id} data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:246:19"}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <label data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:252:13"}>
              Сумма, ₽
              <input
                autoFocus
                type="number"
                placeholder="0"
                value={expenseDraft.amount}
                onChange={(e) => setExpenseDraft({ ...expenseDraft, amount: e.target.value })} data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:254:15"}
              />
            </label>
            <button className="primary-button" type="submit" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:262:13"}>
              Сохранить расход
            </button>
          </form>
        )}
        <footer data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:267:9"}>
          <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:268:11"}>Копилка · 2025</span>
          <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:269:11"}>◉ Данные сохраняются на устройстве</span>
        </footer>
      </div>
      <nav className="bottom-nav" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:272:7"}>
        <a className="active" data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:273:9"}>
          <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:274:11"}>⌂</span>Обзор
        </a>
        <a data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:276:9"}>
          <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:277:11"}>▦</span>Календарь
        </a>
        <a data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:279:9"}>
          <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:280:11"}>＋</span>Расход
        </a>
        <a data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:282:9"}>
          <span data-amazi-id={"семеиныи-бюджет/src/features/dashboard/budget-dashboard.tsx:283:11"}>◌</span>Ещё
        </a>
      </nav>
    </main>
  );
};
